import { useState, useMemo } from 'react';
import { MusikkKategori } from '@/services/api';

interface UseCategoryManagementProps {
  kategorier: MusikkKategori[];
  selectedCategories: string[];
  onSelectedCategoriesChange: (categories: string[]) => void;
}

export const useCategoryManagement = ({
  kategorier,
  selectedCategories,
  onSelectedCategoriesChange,
}: UseCategoryManagementProps) => {
  const [categoryInput, setCategoryInput] = useState('');
  const [showKategoriDropdown, setShowKategoriDropdown] = useState(false);
  const [newKategoriWarning, setNewKategoriWarning] = useState<string | null>(null);

  // Hjelpefunksjon for å finne match
  const findMatchingKategori = (input: string): MusikkKategori | null => {
    const normalizedInput = input.trim().toLowerCase();
    if (!normalizedInput) return null;
    
    // 1. Eksakt match (case-insensitive)
    const exactMatch = kategorier.find(
      k => k.navn.toLowerCase() === normalizedInput
    );
    if (exactMatch) return exactMatch;
    
    // 2. Starts-with match (case-insensitive)
    const startsWithMatch = kategorier.find(
      k => k.navn.toLowerCase().startsWith(normalizedInput)
    );
    if (startsWithMatch) return startsWithMatch;
    
    return null;
  };

  // Hjelpefunksjon for å sjekke om kategori er for lik en allerede valgt kategori
  const isTooSimilarToSelected = (kategoriNavn: string): boolean => {
    const katLower = kategoriNavn.toLowerCase();
    return selectedCategories.some(selected => {
      const selectedLower = selected.toLowerCase();
      return katLower !== selectedLower && 
             (katLower.includes(selectedLower) || selectedLower.includes(katLower));
    });
  };

  // Filtrerte kategorier for dropdown
  const filteredKategorier = useMemo(() => {
    return kategorier
      .filter((kat) => {
        const katNavn = kat.navn.toLowerCase();
        const input = categoryInput.toLowerCase().trim();
        const isSelected = selectedCategories.includes(kat.navn);
        const isTooSimilar = isTooSimilarToSelected(kat.navn);
        return katNavn.includes(input) && !isSelected && !isTooSimilar;
      })
      .sort((a, b) => {
        const aLower = a.navn.toLowerCase();
        const bLower = b.navn.toLowerCase();
        const input = categoryInput.toLowerCase().trim();
        
        if (aLower === input) return -1;
        if (bLower === input) return 1;
        if (aLower.startsWith(input)) return -1;
        if (bLower.startsWith(input)) return 1;
        return 0;
      });
  }, [kategorier, categoryInput, selectedCategories]);

  const handleCategoryInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCategoryInput(value);
    setShowKategoriDropdown(value.trim().length > 0);
    setNewKategoriWarning(null);
  };

  const handleCategorySelect = (kategori: MusikkKategori) => {
    if (!selectedCategories.includes(kategori.navn)) {
      onSelectedCategoriesChange([...selectedCategories, kategori.navn]);
    }
    setCategoryInput('');
    setShowKategoriDropdown(false);
    setNewKategoriWarning(null);
  };

  const handleAddCategory = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && categoryInput.trim()) {
      e.preventDefault();
      
      const match = findMatchingKategori(categoryInput);
      
      if (match) {
        if (!selectedCategories.includes(match.navn)) {
          onSelectedCategoriesChange([...selectedCategories, match.navn]);
          setCategoryInput('');
          setShowKategoriDropdown(false);
        }
      } else {
        setNewKategoriWarning(
          `"${categoryInput.trim()}" finnes ikke. Vil du opprette som ny kategori?`
        );
      }
    }
  };

  const confirmNewCategory = () => {
    if (!categoryInput.trim()) return;
    
    if (!selectedCategories.includes(categoryInput.trim())) {
      onSelectedCategoriesChange([...selectedCategories, categoryInput.trim()]);
    }
    setCategoryInput('');
    setNewKategoriWarning(null);
    setShowKategoriDropdown(false);
  };

  const removeCategory = (category: string) => {
    onSelectedCategoriesChange(selectedCategories.filter(c => c !== category));
  };

  return {
    categoryInput,
    showKategoriDropdown,
    newKategoriWarning,
    filteredKategorier,
    setCategoryInput,
    setShowKategoriDropdown,
    setNewKategoriWarning,
    handleCategoryInputChange,
    handleCategorySelect,
    handleAddCategory,
    confirmNewCategory,
    removeCategory,
  };
};

